import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { APP_DEFAULT_TZ, formatDateRange, formatLongDate } from '@utils/dateUtils';
import type { IInvestigationReport } from '@apis/investigationReports';
import { RecentCardShell } from '../RecentReports/RecentCardShell';
import shellStyles from '../RecentReports/RecentCardShell.module.css';

interface IProjectOverviewRecentCardProps {
  submission: IInvestigationReport;
  siteId: string;
}

/**
 * Recent-rail rendering for a Project Overview submission.
 *
 * Project Overview archives an empty `description`, so the fallback
 * `DefaultRecentCard` would render a blank meta row (no date). This card
 * renders the report's subject window instead: a single day for DATE-mode
 * submissions, a `from – to` range for PERIOD-mode ones — mirroring the
 * `dateText` shown on the report page's own header.
 */
export function ProjectOverviewRecentCard({ submission, siteId }: IProjectOverviewRecentCardProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'ja' ? 'ja' : 'en';
  const tz = useSelector(selectSiteTz) ?? APP_DEFAULT_TZ;

  // PERIOD → range; DATE → single day. Nothing at all for the legacy records
  // that predate `dateFrom`/`dateTo` being persisted: this report's subject is
  // the window and nothing else on the record stands in for it. Falling back
  // to the archived instant would print the moment the report was created in
  // the slot that means "the days this report covers" — and `RecentCardShell`
  // already shows the creation time, labelled as such, in its top-right slot.
  const dateText =
    submission.dateFrom && submission.dateTo
      ? formatDateRange(submission.dateFrom, submission.dateTo, lang, tz)
      : submission.dateFrom
        ? formatLongDate(submission.dateFrom, lang, tz)
        : '';

  // Title fallback — legacy / mock records may submit an empty title; the
  // i18n key keeps the rail row identifiable.
  const title =
    submission.title && submission.title.length > 0
      ? submission.title
      : t('reports.projectOverview.title');

  return (
    <RecentCardShell
      reportType="projectOverview"
      reportId={submission.reportId}
      siteId={siteId}
      createdAtIso={submission.dateGeneratedIso}
      title={title}
    >
      {dateText && <span className={shellStyles.subtitle}>{dateText}</span>}
    </RecentCardShell>
  );
}
